import React, { useRef, useState, useEffect, Fragment, useMemo, useCallback, memo } from 'react';
import './offresCompatibles.css';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Trans, useTranslation } from 'react-i18next';
import { Tag } from 'primereact/tag';
import { IoHeartOutline, IoHeart } from 'react-icons/io5';
import { requestIdleCallback } from '../utils/performanceUtils';

import { enumHttpError } from '../enums/enumHttpError.js';
import { enumCategorieRecherche } from '../enums/enumCategorieRecherche.js';
import { enumSearchOuLabel } from '../enums/enumSearchOuLabel.js';
import RechercheService from '../services/rechercheService.js';
import Helper from '../utils/helper.js';
import parse from 'html-react-parser';

import { useAuthState } from "../context/context";
import { useOffersSearch } from '../hooks/useOffersSearch';
import { useProfileFilters } from '../hooks/useProfileFilters';

import { OffreEmploiThumbnail } from '../components/thumbnail/offreEmploiThumbnail.js';
import { ThumbnailPaginator } from '../components/paginator/thumbnailPaginator';
import { ListeElementsRecherche } from '../scenes/rechercheAffinee/listeElementsRecherche';
import { FilterMultiSelect } from '../components/multiselect/filterMultiSelect';
import { ContractTypeFilters } from '../components/filters/ContractTypeFilters';
import MenuPopUpOffersFilter from './offresCompatibles/menu/MenuPopUpOffersFilter';
import RechercheContext from '../scenes/rechercheAffinee/RechercheContext';

export const OffresCompatibles = (props) => {
    const toast = useRef(null);
    const resultsContainerRef = useRef(null);
    const { t } = useTranslation();
    const currentUser = useAuthState();
    
    const adminImpersonatingEtudiantId = props.history?.location?.state?.adminImpersonatingEtudiantId;

    // Custom hooks
    const {
        loading,
        totalCount,
        setTotalCount,
        offresEmploi,
        setOffresEmploi,
        error,
        initialLoadComplete,
        setInitialLoadComplete,
        showFavoritesOnly,
        toggleFavoritesOnly,
        lazyParams,
        setLazyParams,
        loadLazyData
    } = useOffersSearch(currentUser, adminImpersonatingEtudiantId);

    const { profileFiltersLoading, loadStudentProfileFilters } = useProfileFilters(currentUser, adminImpersonatingEtudiantId);

    const [activeIndex, setActiveIndex] = useState([0, 1, 2, 3, 4]);
    const [tokenResetFilters, setTokenResetFilters] = useState(0);

    const rechercheService = new RechercheService();

    const STORAGE_KEY = 'offresCompatibles_state';

    // Function to save state to localStorage
    const saveStateToStorage = (state) => {
        try {
            const stateToSave = {
                lazyParams: state.lazyParams,
                showFavoritesOnly: state.showFavoritesOnly,
                activeIndex: state.activeIndex,
                timestamp: Date.now()
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
        } catch (error) {
            console.warn('Erreur lors de la sauvegarde dans localStorage:', error);
        }
    };

    // Function to load state from localStorage
    const loadStateFromStorage = () => {
        try {
            const savedState = localStorage.getItem(STORAGE_KEY);
            if (savedState) {
                const parsedState = JSON.parse(savedState);
                // Vérifier que l'état n'est pas trop ancien (ex: 1 heure)
                const oneHour = 60 * 60 * 1000;
                if (Date.now() - parsedState.timestamp < oneHour) {
                    return parsedState;
                }
            }
        } catch (error) {
            console.warn('Erreur lors du chargement depuis localStorage:', error);
        }
        return null;
    };

    // Function to clear the localStorage
    const clearStateFromStorage = () => {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (error) {
            console.warn('Erreur lors du nettoyage du localStorage:', error);
        }
    };

    // Initialize page params with restoration from localStorage
    useEffect(() => {
        let mounted = true;
        
        const initializePage = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const search = parseInt(urlParams.get('page'));
            
            // Try to restore state from localStorage
            const savedState = loadStateFromStorage();
            
            if (savedState && mounted) {
                // Restore saved parameters
                setLazyParams(savedState.lazyParams);
                if (savedState.showFavoritesOnly !== showFavoritesOnly) {
                    toggleFavoritesOnly();
                }
                setActiveIndex(savedState.activeIndex);
                
                // Update the URL to reflect the restored state
                const sp = new URLSearchParams();
                sp.set('page', savedState.lazyParams.page.toString());
                sp.set('fav', savedState.showFavoritesOnly ? '1' : '0');
                window.history.replaceState({}, '', window.location.pathname + '?' + sp.toString());
                
                // DO NOT reload profile filters, just mark as completed
                setInitialLoadComplete(true);
            } else if (mounted) {
                // Original behavior if no saved state
                if (!search) {
                    const sp = new URLSearchParams(window.location.search);
                    sp.set('page', '1');
                    sp.set('fav', showFavoritesOnly ? '1' : '0');
                    window.history.pushState({}, '', window.location.pathname + '?' + sp.toString());
                }

                await loadStudentProfileFiltersAndData();
            }
        };
        
        initializePage();

        return () => {
            mounted = false;
            setOffresEmploi([]);
        };
    }, []); // Dépendances vides pour éviter la boucle

    // Save state and load data (combined to avoid double call)
    useEffect(() => {
        if (initialLoadComplete) {
            // Save asynchronously to avoid blocking
            requestIdleCallback(() => {
                saveStateToStorage({
                    lazyParams,
                    showFavoritesOnly,
                    activeIndex
                });
            }, { timeout: 100 });
            
            loadLazyData();
        }
    }, [lazyParams, showFavoritesOnly, initialLoadComplete, loadLazyData]);
    
    // Save activeIndex separately (no need to reload data)
    useEffect(() => {
        if (initialLoadComplete) {
            requestIdleCallback(() => {
                saveStateToStorage({
                    lazyParams,
                    showFavoritesOnly,  
                    activeIndex
                });
            }, { timeout: 100 });
        }
    }, [activeIndex]);

    // Scroll to top on page change (optimisé avec RAF)
    useEffect(() => {
        if (initialLoadComplete && lazyParams.page > 1) {
            requestAnimationFrame(() => {
                try {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                } catch (error) {
                    console.warn('Scroll error caught and handled:', error);
                }
            });
        }
    }, [lazyParams.page]);

    const loadStudentProfileFiltersAndData = async () => {
        try {
            const defaultFilters = await loadStudentProfileFilters();
            
            if (defaultFilters.length > 0) {
                setLazyParams(prevParams => {
                    const merged = [...prevParams.filters, ...defaultFilters];
                    const seen = new Set();
                    const unique = merged.filter(f => {
                        if (f?.categorie !== undefined && f.value !== undefined) {
                            const key = `${f.categorie}|${String(f.value)}`;
                            if (!seen.has(key)) {
                                seen.add(key);
                                return true;
                            }
                        }
                        return false;
                    });
                    
                    return {
                        ...prevParams,
                        filters: unique,
                        first: 0,
                        page: 1
                    };
                });
            }
            
            setInitialLoadComplete(true);
        } catch (error) {
            setInitialLoadComplete(true);
        }
    };

    const handleChange = useCallback((recherche) => {
        if (typeof recherche === "object" && recherche !== null) {
            if (!Helper.containsValueAndCategorie([...lazyParams.filters], recherche.value, recherche.categorie)) {
                setLazyParams(prevParams => ({
                    ...prevParams,
                    filters: [...prevParams.filters, recherche],
                    first: 0,
                    page: 1
                }));
            }
        }
    }, [lazyParams.filters]);

    const onCheckChange = useCallback((checked, categorie) => {
        setLazyParams(prevParams => {
            let _filters = [...prevParams.filters];
            const contractCategories = [
                enumCategorieRecherche.ContratStage,
                enumCategorieRecherche.ContratAltApp,
                enumCategorieRecherche.ContratAltPro,
                enumCategorieRecherche.ContratCDDCDI,
                enumCategorieRecherche.ContratAutres
            ];

            if (contractCategories.includes(categorie)) {
                _filters = _filters.filter(f => f.categorie !== categorie);
                if (checked) {
                    _filters.push({ categorie, value: true });
                }
            } else {
                const index = Helper.findIndexByCategorie(_filters, categorie);
                if (index === -1 && checked) {
                    _filters.push({ categorie, value: checked });
                } else if (index !== -1) {
                    if (checked) {
                        _filters[index] = { categorie, value: checked };
                    } else {
                        _filters.splice(index, 1);
                    }
                }
            }

            return {
                ...prevParams,
                filters: _filters,
                first: 0,
                page: 1
            };
        });
    }, []);

    const handleMultiSelectChange = useCallback((values, categoriesToClean, targetCategorie) => {
        setLazyParams(prevParams => {
            let _filters = [...prevParams.filters];
            
            // Remove existing filters for target category
            _filters = _filters.filter(item => item.categorie !== targetCategorie);
            
            // Clean up related categories if specified
            if (categoriesToClean) {
                _filters = _filters.filter(item => !categoriesToClean.includes(item.categorie));
            }
            
            // Add new filters
            values.forEach(e => _filters.push(e));
            
            return {
                ...prevParams,
                filters: _filters,
                first: 0,
                page: 1
            };
        });
    }, []);

    const clearAllFilters = useCallback(() => {
        setLazyParams(prevParams => ({
            ...prevParams,
            filters: [],
            first: 0,
            page: 1
        }));
        setTokenResetFilters(Date.now());
        // Also clear localStorage when clearing all filters
        requestIdleCallback(() => clearStateFromStorage(), { timeout: 100 });
    }, []);

    const refreshWithProfile = useCallback(async () => {
        if (profileFiltersLoading) return;
        
        clearAllFilters();
        // Clear localStorage before reloading the profile
        requestIdleCallback(() => clearStateFromStorage(), { timeout: 100 });
        setTimeout(async () => {
            await loadStudentProfileFiltersAndData();
        }, 50);
    }, [profileFiltersLoading, clearAllFilters]);

    const searchLocalisation = (query) => {
        return rechercheService.getRechercheLocalisationOffreEmploiById(query, false).then(res => {

            let stringifyRechercheOu = JSON.stringify(res);
            stringifyRechercheOu = stringifyRechercheOu.replace('villesLabel', t('search.villesLabel'));
            stringifyRechercheOu = stringifyRechercheOu.replace('departementsLabel', t('search.departementsLabel'));
            stringifyRechercheOu = stringifyRechercheOu.replace('regionsLabel', t('search.regionsLabel'));
            stringifyRechercheOu = stringifyRechercheOu.replace('paysLabel', t('search.paysLabel'));
            stringifyRechercheOu = stringifyRechercheOu.replace('etablissementLabel', t('search.etablissementLabel'));

            let _villesIndex = res.map(function (e) { return e.label }).indexOf(enumSearchOuLabel.VILLE)
            let _departementsIndex = res.map(function (e) { return e.label }).indexOf(enumSearchOuLabel.DEPARTEMENT)
            let _regionsIndex = res.map(function (e) { return e.label }).indexOf(enumSearchOuLabel.REGION)
            let _paysIndex = res.map(function (e) { return e.label }).indexOf(enumSearchOuLabel.PAYS)
            let _indexesOfCategoriesToRemove = [];

            if (res[_villesIndex].items.length == 0)
                _indexesOfCategoriesToRemove.push(_villesIndex)
            else
                res[_villesIndex].label = t('search.villesLabel');

            if (res[_departementsIndex].items.length == 0)
                _indexesOfCategoriesToRemove.push(_departementsIndex)
            else
                res[_departementsIndex].label = t('search.departementsLabel');

            if (res[_regionsIndex].items.length == 0)
                _indexesOfCategoriesToRemove.push(_regionsIndex)
            else
                res[_regionsIndex].label = t('search.regionsLabel');

            if (res[_paysIndex].items.length == 0)
                _indexesOfCategoriesToRemove.push(_paysIndex)
            else
                res[_paysIndex].label = t('search.paysLabel');

            _indexesOfCategoriesToRemove.sort(function (a, b) { return b - a });
            _indexesOfCategoriesToRemove.forEach(i => {
                res.splice(i, 1);
            });

            return res;
        });
    };

    const searchAppellation = (query) => {
        return rechercheService.getRechercheAppellationFunnel(query, lazyParams);
    };

    const handleUnlike = useCallback((offreEmploiId) => {
        setOffresEmploi(prevOffres => 
            prevOffres.map(oe => 
                oe.id === offreEmploiId 
                    ? { ...oe, isFavorite: false } 
                    : oe
            )
        );
    }, []);

    const tabHeaderTemplate = useCallback((name, values) => {
        const filterCount = lazyParams?.filters?.filter(o => values.includes(o.categorie)).length || 0;
        return (
            <div className='msf-tab-header'>
                <div className='msf-tab-subheader'>
                    <div className="msf-tab-title-filter">{name}</div>
                </div>
                <div>
                    {filterCount > 0 && (
                        <Tag className="oc-filter-count-tag">
                            {filterCount}
                        </Tag>
                    )}
                </div>
            </div>
        );
    }, [lazyParams.filters]);

    const filterContext = useMemo(() => ({
        lazyParams,
        setLazyParams,
        categorieRecherche: enumCategorieRecherche,
        tokenResetFilters,
        rechercheApi: () => {}
    }), [lazyParams, tokenResetFilters]);

    const contextRecherche = useMemo(() => ({
        lazyParams,
        setLazyParams,
        rechercheApi: (lp) => setLazyParams(lp),
        categorieRecherche: enumCategorieRecherche,
        tokenResetFilters
    }), [lazyParams, tokenResetFilters]);

    const renderFiltersAccordion = useMemo(() => (idSuffix = '') => (
        <div className={showFavoritesOnly ? 'oc-filters-disabled' : ''}>
            <Accordion 
                multiple 
                activeIndex={activeIndex} 
                onTabChange={(e) => setActiveIndex(e.index)} 
                expandIcon="pi pi-chevron-down" 
                collapseIcon="pi pi-chevron-up"
            >
                <AccordionTab disabled={showFavoritesOnly} header={tabHeaderTemplate(t('offers.contract_type'), [enumCategorieRecherche.ContratStage, enumCategorieRecherche.ContratAltApp, enumCategorieRecherche.ContratAltPro, enumCategorieRecherche.ContratCDDCDI, enumCategorieRecherche.ContratAutres])}>
                    <ContractTypeFilters 
                        lazyParams={lazyParams} 
                        onCheckChange={onCheckChange} 
                        idSuffix={idSuffix}
                    />
                </AccordionTab>

                <AccordionTab disabled={showFavoritesOnly} header={tabHeaderTemplate(t('general.location'), [enumCategorieRecherche.Ville, enumCategorieRecherche.Departement, enumCategorieRecherche.Region, enumCategorieRecherche.Pays])}>
                    <ListeElementsRecherche
                        values={[enumCategorieRecherche.Ville, enumCategorieRecherche.Departement, enumCategorieRecherche.Region, enumCategorieRecherche.Pays]}
                        onChange={handleChange}
                        onSearch={searchLocalisation}
                        field="label"
                        placeholder={t('search.localisation_placeholder_filter')}
                        // placeholder="Département"
                        icon="pi pi-map-marker"
                        optionGroupLabel="label"
                        optionGroupChildren="items"
                        style="student"
                    />
                </AccordionTab>

                <AccordionTab disabled={showFavoritesOnly} header={tabHeaderTemplate(t('search.domainesLabel'), [enumCategorieRecherche.DipDomaine])}>
                    <FilterMultiSelect
                        className='msf-diplome-multiselect'
                        classNameButton='dmp-button-student-style'
                        values={[enumCategorieRecherche.DipDomaine]}
                        placeholder={t('search.select') + '...'}
                        contextRecherche={filterContext}
                        onChange={(values, mustCleanUp) => handleMultiSelectChange(
                            values,
                            mustCleanUp ? [enumCategorieRecherche.DipMetier, enumCategorieRecherche.Appellation] : null,
                            enumCategorieRecherche.DipDomaine
                        )}
                        label='domaineType'
                        setUnselectTokenDipDomaine={() => {}}
                        setUnselectTokenDipMetier={() => {}}
                        setUnselectTokenAppellation={() => {}}
                    />
                </AccordionTab>

                <AccordionTab disabled={showFavoritesOnly} header={tabHeaderTemplate(t('search.sousDomainesLabel'), [enumCategorieRecherche.DipMetier])}>
                    <FilterMultiSelect
                        className='msf-diplome-multiselect'
                        classNameButton='dmp-button-student-style'
                        values={[enumCategorieRecherche.DipMetier]}
                        placeholder={t('search.select') + '...'}
                        contextRecherche={filterContext}
                        onChange={(values, mustCleanUp) => handleMultiSelectChange(
                            values,
                            mustCleanUp ? [enumCategorieRecherche.Appellation] : null,
                            enumCategorieRecherche.DipMetier
                        )}
                        label='metierType'
                        setUnselectTokenDipDomaine={() => {}}
                        setUnselectTokenDipMetier={() => {}}
                        setUnselectTokenAppellation={() => {}}
                    />
                </AccordionTab>

                <AccordionTab disabled={showFavoritesOnly} header={tabHeaderTemplate(t('search.metiersLabel'), [enumCategorieRecherche.Appellation])}>
                    <ListeElementsRecherche
                        contextRecherche={{ lazyParams, categorieRecherche: enumCategorieRecherche }}
                        filter
                        values={[enumCategorieRecherche.Appellation]}
                        onChange={handleChange}
                        onSearch={searchAppellation}
                        field='label'
                        placeholder={t('search.metiersPlaceholder')}
                        icon='pi pi-search'
                        style="student"
                        canDeleteAll
                    />
                </AccordionTab>
            </Accordion>
        </div>
    ), [showFavoritesOnly, activeIndex, lazyParams, tabHeaderTemplate, onCheckChange, handleChange, handleMultiSelectChange, filterContext, t]);

    return (
        <RechercheContext.Provider value={contextRecherche}>
            <div className={`oc-page ${props.className || ''}`}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <h2 style={{ margin: 0 }}><Trans i18nKey='offers.page_title.full' /></h2>
                    <button
                        className={`oc-only-mobile oc-favorites-button ${showFavoritesOnly ? 'active' : ''}`}
                        onClick={toggleFavoritesOnly}
                        aria-pressed={showFavoritesOnly}
                        title={showFavoritesOnly ? t('offers.favorites_offers') : t('offers.show_favorites')}
                    >
                        {showFavoritesOnly ? <IoHeart size={16} /> : <IoHeartOutline size={16} />}
                        <span><Trans i18nKey='menu.my_favorites' /></span>
                    </button>
                </div>
                
                <div className="p-grid mt-5">
                    {/* Filter Sidebar */}
                    <div className="p-col-12 p-md-3">
                        <div className="rechercheaffinee">
                            <div className="filter-header">
                                <h3>{t('general.filters')}</h3>
                                <div className="filter-buttons">
                                    <Button 
                                        icon="pi pi-user" 
                                        label={t('general.myProfile.light')}
                                        className="filter-action-btn btn-terra p-button-rounded p-button-sm"
                                        onClick={refreshWithProfile}
                                        disabled={showFavoritesOnly || profileFiltersLoading}
                                        loading={profileFiltersLoading}
                                        title={showFavoritesOnly ? t('offers.filters_disabled_in_favorites') : ''}
                                    />
                                    <Button
                                        icon="pi pi-heart"
                                        label={t('offers.show_favorites')}
                                        className={`filter-action-btn btn-terra-outline btn-favorites p-button-rounded p-button-sm ${showFavoritesOnly ? 'active' : ''}`}
                                        onClick={toggleFavoritesOnly}
                                        aria-pressed={showFavoritesOnly}
                                        title={showFavoritesOnly ? t('offers.favorites_offers') : t('offers.show_favorites')}
                                    />
                                    <Button 
                                        icon="pi pi-filter-slash"
                                        label={t('search.erase_all_filters')}
                                        className="filter-action-btn btn-terra-outline p-button-rounded p-button-sm"
                                        onClick={clearAllFilters}
                                        disabled={showFavoritesOnly}
                                        title={showFavoritesOnly ? t('offers.filters_disabled_in_favorites') : ''}
                                    />
                                </div>
                            </div>

                            {renderFiltersAccordion()}
                        </div>
                    </div>

                    {/* Results Section */}
                    <div className="p-col-12 p-md-9">
                        <div className="rechercheAffineeResultats" ref={resultsContainerRef}>
                            <div className="results-header">
                                <span>{totalCount} {t('general.results')}</span>
                            </div>

                            { (loading || profileFiltersLoading) ? (
                                <div style={{ minHeight: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <ProgressSpinner />
                                </div>
                            ) : (
                                <>
                                    <div className='p-grid'>
                                        {offresEmploi.map((offreEmploi, indexOE) => (
                                            <Fragment key={`${offreEmploi.id}~${indexOE}`}>
                                                <div className='p-col-12 p-sm-12 p-md-6 p-lg-4'>
                                                    <OffreEmploiThumbnail offreEmploi={offreEmploi} onUnlike={() => handleUnlike(offreEmploi.id)} />
                                                </div>
                                            </Fragment>
                                        ))}
                                    </div>

                                    {offresEmploi?.length > 0 && (
                                        <ThumbnailPaginator lazyParams={lazyParams} setLazyParams={setLazyParams} totalRecords={totalCount} />
                                    )}

                                    { !showFavoritesOnly && offresEmploi.length === 0 && error !== 'failed' && error !== enumHttpError.CANCELED && <div className="center-element-oc">{parse(t("offers.no_offers"))}</div> }
                                    { !showFavoritesOnly && offresEmploi.length === 0 && error === 'failed' && <div className="center-element-oc"><Trans i18nKey="offers.server_connexion_error" /></div> }
                                    { showFavoritesOnly && offresEmploi.length === 0 && error !== 'failed' && <div className="center-element-oc"><Trans i18nKey="offers.no_favorites" /></div> }
                                    { showFavoritesOnly && offresEmploi.length === 0 && error === 'failed' && <div className="center-element-oc"><Trans i18nKey="offers.server_connexion_error" /></div> }
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Mobile Filter Button */}
                {!showFavoritesOnly && (
                    <div className='oc-refined-search-float-button oc-only-mobile'>
                        <MenuPopUpOffersFilter 
                            appliedCount={lazyParams?.filters?.length || 0} 
                            onClearAll={clearAllFilters}
                            onClickProfile={refreshWithProfile}
                            profileLabel={t('general.myProfile.light')}
                            profileDisabled={showFavoritesOnly || profileFiltersLoading}
                            profileLoading={profileFiltersLoading}
                        >
                            {renderFiltersAccordion('_m')}
                        </MenuPopUpOffersFilter>
                    </div>
                )}
                <Toast ref={toast} />
            </div>
        </RechercheContext.Provider>
    );
};